// src/contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react'
import { jwtDecode } from 'jwt-decode'
import { config } from "../config";

const API_BASE_URL = config.API_URL;

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Base API request function with better error handling
const baseApiRequest = async (endpoint, options = {}) => {
  const defaultOptions = {
    headers: {},
    mode: 'cors',
  };

  // Get token from localStorage for authenticated requests
  const token = localStorage.getItem('admin_token')
  if (token && !endpoint.includes('/auth/token')) {
    defaultOptions.headers['Authorization'] = `Bearer ${token}`
  }

  // Handle different content types
  if (options.body instanceof URLSearchParams) {
    // For x-www-form-urlencoded, let the browser set the content type
    defaultOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded'
  } else if (!(options.body instanceof FormData)) {
    defaultOptions.headers['Content-Type'] = 'application/json'
  }

  // Stringify JSON body
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData) && !(options.body instanceof URLSearchParams)) {
    options.body = JSON.stringify(options.body)
  }

  const requestConfig = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, requestConfig)
    // Handle 404 specifically
    if (response.status === 404) {
      throw new Error('API endpoint not found. Please check the backend URL.')
    }

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch (parseError) {
        errorData = { detail: `HTTP error! status: ${response.status}` }
      }
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('API request failed:', error)
     if (error.message.includes('Failed to fetch')) {
      throw new Error('Cannot connect to server. Please check your internet connection and ensure the backend is running.')
    }
    
    throw error
  
  }
}

// Auth-specific API functions
const authAPI = {
  login: async (email, password) => {
    const formData = new URLSearchParams()
    formData.append('username', email)
    formData.append('password', password)

    return baseApiRequest('/auth/token', {
      method: 'POST',
      body: formData,
    })
  },

  logout: async () => {
    return baseApiRequest('/auth/logout', {
      method: 'POST',
    })
  },

  getCurrentUser: async () => {
    return baseApiRequest('/auth/me')
  },

  refreshToken: async (refreshToken) => {
    return baseApiRequest('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
  },

  register: async (userData) => {
    return baseApiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  },
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('admin_token'))

  // Function to validate token
  const validateToken = (token) => {
    try {
      const decoded = jwtDecode(token)
      const currentTime = Date.now() / 1000
      if (decoded.exp < currentTime) {
        return { valid: false, error: 'Token expired' }
      }

      return {
        valid: true,
        user: {
          email: decoded.sub,
          role: decoded.role || 'admin',
          full_name: decoded.full_name,
          user_id: decoded.user_id
        }
      }
    } catch (error) {
      return { valid: false, error: 'Invalid token' }
    }
  }

  const checkAuthStatus = async () => {
    const storedToken = localStorage.getItem('admin_token')

    if (!storedToken) {
      setLoading(false)
      return
    }

    try {
      const localValidation = validateToken(storedToken)

      if (!localValidation.valid) {
        console.warn('Token validation failed:', localValidation.error)
        logout()
        return
      }

      // Verify with backend
      const userData = await authAPI.getCurrentUser()
      setToken(storedToken)
      setIsAuthenticated(true)
      setUser(userData)
    } catch (error) {
      console.error('Error during auth check:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const login = async (email, password) => {
    try {
      setLoading(true)
      const response = await authAPI.login(email, password)

      if (response.access_token) {
        localStorage.setItem('admin_token', response.access_token)
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token)
        }

        setToken(response.access_token)
        setIsAuthenticated(true)
        setUser(response.user || {
          email: response.email,
          full_name: response.full_name,
          role: response.role,
          user_id: response.user_id
        })

        return { success: true }
      } else {
        throw new Error('No access token received')
      }
    } catch (error) {
      console.error('Login error:', error)

      let errorMessage = 'Login failed. Please check your credentials.'
      if (error.message.includes('401') || error.message.includes('Incorrect')) {
        errorMessage = 'Invalid email or password.'
      } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection.'
      } else {
        errorMessage = error.message
      }

      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('refresh_token')
    setToken(null)
    setIsAuthenticated(false)
    setUser(null)

    authAPI.logout().catch(error => {
      console.error('Logout API call failed:', error)
    })
  }

  const value = {
    isAuthenticated,
    user,
    loading,
    token,
    login,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider